"use client";

import { useTranslations } from "next-intl";

export interface SheetMetalDesignGuidelinesProps {
	/** 翻译命名空间：title、description、三列表头、row1～row8 的 Feature / Value / Note */
	translationNamespace?: string;
}

/** 八行数据对应的翻译键名 */
const GUIDELINE_ROWS: readonly { featureKey: string; valueKey: string; noteKey: string }[] = [
	{ featureKey: "row1Feature", valueKey: "row1Value", noteKey: "row1Note" },
	{ featureKey: "row2Feature", valueKey: "row2Value", noteKey: "row2Note" },
	{ featureKey: "row3Feature", valueKey: "row3Value", noteKey: "row3Note" },
	{ featureKey: "row4Feature", valueKey: "row4Value", noteKey: "row4Note" },
	{ featureKey: "row5Feature", valueKey: "row5Value", noteKey: "row5Note" },
	{ featureKey: "row6Feature", valueKey: "row6Value", noteKey: "row6Note" },
	{ featureKey: "row7Feature", valueKey: "row7Value", noteKey: "row7Note" },
	{ featureKey: "row8Feature", valueKey: "row8Value", noteKey: "row8Note" },
];

const thBase =
	"px-3 py-4 text-center text-xs font-bold leading-snug text-white sm:px-4 sm:py-5 sm:text-sm md:py-5 md:text-base";
/** 行高：加大纵向 padding，单元格更易读 */
const tdBase = "align-middle px-4 py-5 text-black sm:px-6 sm:py-6 md:px-8 md:py-7 lg:px-10";

export default function SheetMetalDesignGuidelines({
	translationNamespace = "sheetMetalFabricationDesignGuidelines",
}: SheetMetalDesignGuidelinesProps) {
	const t = useTranslations(translationNamespace);

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<h2 className="mb-3 text-center text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
					{t("title")}
				</h2>
				<p className="mb-6 text-center text-sm leading-relaxed text-black sm:mb-8 md:mb-10 md:text-base lg:mb-12">
					{t("description")}
				</p>

				{/* 圆角 + 阴影；border-separate + border-spacing-0 以便圆角裁剪 */}
				<div className="overflow-hidden rounded-[15px] shadow-[0_4px_24px_rgba(0,0,0,0.08)] sm:shadow-[0_6px_32px_rgba(0,0,0,0.1)]">
					<table className="w-full border-separate border-spacing-0 text-left">
						<thead>
							<tr>
								<th scope="col" className={`${thBase} rounded-tl-[15px] bg-[#2d5a1f]`}>
									{t("colFeature")}
								</th>
								<th scope="col" className={`${thBase} bg-[#4a8c2a]`}>
									{t("colRecommended")}
								</th>
								<th scope="col" className={`${thBase} rounded-tr-[15px] bg-[#6dad3c]`}>
									{t("colDesignNotes")}
								</th>
							</tr>
						</thead>
						<tbody>
							{GUIDELINE_ROWS.map((row, index) => {
								const isLast = index === GUIDELINE_ROWS.length - 1;
								const b = isLast ? "border-b-0" : "border-b border-gray-200";
								return (
									<tr key={row.featureKey}>
										<td
											className={`${tdBase} ${b} bg-white text-xs font-bold sm:text-sm ${
												isLast ? "rounded-bl-[15px]" : ""
											}`}
										>
											{t(row.featureKey)}
										</td>
										<td className={`${tdBase} ${b} bg-[#f4f6f4] text-center text-xs font-normal sm:text-sm`}>
											{t(row.valueKey)}
										</td>
										<td
											className={`${tdBase} ${b} bg-white text-left text-[11px] font-normal leading-snug sm:text-xs md:text-sm ${
												isLast ? "rounded-br-[15px]" : ""
											}`}
										>
											{t(row.noteKey)}
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			</div>
		</section>
	);
}
